/**
 * Dev tools gate — decides whether the dev-only chrome (today: the
 * `Impersonator` pill) renders at all.
 *
 * - `bun dev` (`import.meta.env.DEV`): always on. Nothing to configure.
 * - Any build (staging, production, Capacitor): off by default, so a URL sent
 *   to a client shows a clean app. Opening the app once with `?dev=1` turns it
 *   on for that browser (persisted in `localStorage`); `?dev=0` turns it off
 *   again — handy to see exactly what a client sees.
 *
 * The param is stripped from the address bar right after it is read, so a
 * URL copied afterwards never carries the switch along. This runs BEFORE the
 * router is created (see `main.tsx`), which is why it uses `history.replaceState`
 * directly instead of a router navigation.
 *
 * Plain `localStorage`, not `kv` from `@bitez/data`: on native `kv` is the
 * Keychain, and a dev flag has no business there.
 */
const STORAGE_KEY = 'bitez.devtools'
const SEARCH_PARAM = 'dev'
const OFF_VALUES = new Set(['0', 'false', 'off'])

let enabled = import.meta.env.DEV

function readStored(): boolean {
  try {
    return window.localStorage.getItem(STORAGE_KEY) === '1'
  } catch {
    return false
  }
}

function writeStored(on: boolean): void {
  try {
    if (on) window.localStorage.setItem(STORAGE_KEY, '1')
    else window.localStorage.removeItem(STORAGE_KEY)
  } catch {
    // private mode / quota — the flag just does not persist
  }
}

/** Call once on boot, before the router exists. */
export function initDevTools(): void {
  if (typeof window === 'undefined') return

  const url = new URL(window.location.href)
  const param = url.searchParams.get(SEARCH_PARAM)

  if (param !== null) {
    writeStored(!OFF_VALUES.has(param))
    url.searchParams.delete(SEARCH_PARAM)
    window.history.replaceState(window.history.state, '', url)
  }

  enabled = import.meta.env.DEV || readStored()
}

export function isDevToolsEnabled(): boolean {
  return enabled
}
